import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { INSTALLATION_STATE_LABEL_KEYS } from '@core/constants';
import { APP_PATHS } from '@core/constants/route.constant';
import { resolveDeviceStates } from '@core/helpers/device-state.helper';
import { installationFailureReasonKey } from '@core/helpers/installation-failure-reason.helper';
import {
  ChannelUsage,
  ChannelUsageEntry,
  DeviceModel,
  InstallationGroupWithAccess,
  LocationModel,
  mapDeviceStatusToBadgeStatus,
  PartnerModel,
} from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardComponent } from '@custom-components/general/card/card.component';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { InlineHeadingComponent } from '@custom-components/general/inline-heading/inline-heading.component';
import { ProgressBarComponent } from '@custom-components/general/progress-bar/progress-bar.component';
import { StatusChipComponent } from '@custom-components/general/status-chip/status-chip.component';
import {
  ConfirmWarningDialogComponent,
  ConfirmWarningDialogData,
} from '@custom-components/overlay/confirm-warning-dialog/confirm-warning-dialog.component';

/** `dm.device_type.protocol` values whose devices have no cellular or LAN connection of their own,
 * lowercased for comparison. The casing in the database is not consistent — SmartConnect is stored
 * as `mqtt` while the CHECK constraint on the column spells it `MQTT`, and SQL Server's
 * case-insensitive collation accepts either — so never compare these verbatim.
 * Empty/unknown is deliberately not in here, so the card still shows while the detail is loading. */
const SIMLESS_PROTOCOLS = new Set<string>(['mqtt']);

/** 1NCE tops a SIM up by one fixed bundle per call — the size is set by their product, not by us or
 * by the caller — so the menu can name the amount before the user commits to it. */
const TOP_UP_VOLUME_MB = 500;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The device read returns SQL Server `CONVERT` output rather than strict ISO 8601 — the end date as
 * `2029-03-18` (style 23) and the activation date as `2019-03-18 00:00:00.000` (style 121). `Date`
 * only parses the latter once the space becomes a `T`. */
function parseSimDate(value: string | null | undefined): Date | null {
  if (!value?.trim()) return null;
  const parsed = new Date(value.trim().replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Whole calendar months from `from` to `to`, so a 31-month gap reads as "2 yr 7 mo" rather than
 * being rounded off a 30-day average. */
function wholeMonthsBetween(from: Date, to: Date): number {
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  return Math.max(to.getDate() < from.getDate() ? months - 1 : months, 0);
}

function formatMb(mb: number): string {
  return mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

const LIFETIME_KEY_PREFIX = 'devices.detailTabs.sim.';

function lifetimeRemaining(now: Date, endDate: Date): LifetimeRemaining {
  if (endDate.getTime() <= now.getTime()) {
    return { key: `${LIFETIME_KEY_PREFIX}lifetimeExpired`, params: {} };
  }

  const monthsLeft = wholeMonthsBetween(now, endDate);
  const years = Math.floor(monthsLeft / 12);
  const months = monthsLeft % 12;

  if (years && months) {
    return { key: `${LIFETIME_KEY_PREFIX}lifetimeYearsMonths`, params: { years, months } };
  }
  if (years) return { key: `${LIFETIME_KEY_PREFIX}lifetimeYears`, params: { years } };
  if (months) return { key: `${LIFETIME_KEY_PREFIX}lifetimeMonths`, params: { months } };
  // Under a month left, where "0 mo" would read as already expired.
  const days = Math.max(Math.ceil((endDate.getTime() - now.getTime()) / MS_PER_DAY), 1);
  return { key: `${LIFETIME_KEY_PREFIX}lifetimeDays`, params: { days } };
}

/** Translation key plus its params, rather than finished text: resolving it through the `translate`
 * pipe in the template keeps the headline following a language switch, which `translate.instant`
 * inside a computed would not. */
interface LifetimeRemaining {
  key: string;
  params: Record<string, number>;
}

interface SimLifetime {
  activationDate: Date;
  endDate: Date;
  /** Days still to run, not days elapsed: the bar drains as the SIM ages, so it reads the same
   * direction as the data-remaining bar beside it. */
  remainingDays: number;
  totalDays: number;
  remaining: LifetimeRemaining;
  expired: boolean;
}

interface SimData {
  remainingMb: number;
  totalMb: number;
  remainingLabel: string;
  totalLabel: string;
}

@Component({
  selector: 'xemex-device-general-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    RouterLink,
    TranslateModule,
    CardComponent,
    CardGridComponent,
    DetailFieldComponent,
    InlineHeadingComponent,
    ProgressBarComponent,
    StatusChipComponent,
  ],
  templateUrl: './device-general-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceGeneralTabComponent {
  deviceDetail = input<DeviceModel>();
  partnerRes = input<PartnerModel>();
  locationRes = input<LocationModel>();
  isLoading = input<boolean>(false);
  showConnectionTest = input<boolean>(false);
  isExecutingConnectionTest = input<boolean>(false);
  showFirmwareUpdate = input<boolean>(false);
  showExportCsv = input<boolean>(false);
  installationGroups = input<InstallationGroupWithAccess[]>([]);
  installationGroupsLoading = input<boolean>(false);
  showInstallationGroups = input<boolean>(false);
  /** Top up/extend are Xemex-only actions (matches the backend's auth) — refresh is available to
   * both Xemex and the owning partner's users, same as get_device's auth fallback. */
  isXemex = input<boolean>(false);
  isRefreshingSim = input<boolean>(false);
  isTopUpInProgress = input<boolean>(false);
  isExtendInProgress = input<boolean>(false);
  showInstallations = input<boolean>(false);
  channelUsage = input<ChannelUsage | null>(null);

  companyClicked = output<string>();
  locationClicked = output<number>();
  connectionTestClicked = output<void>();
  firmwareUpdateClicked = output<void>();
  exportCsvClicked = output<void>();
  installationGroupClicked = output<string>();
  refreshSimClicked = output<void>();
  /** Emitted only after the user confirms the ConfirmWarningDialog naming the SIM being acted on
   * — the container performs the actual DeviceService call and re-syncs via refreshSimClicked. */
  topUpSimConfirmed = output<void>();
  extendSimConfirmed = output<void>();

  private readonly dialog = inject(MatDialog);

  /** Translation key of the stored failure reason; null for a code this portal does not know, so
   * the template falls back to the raw text. */
  protected readonly failureReasonKey = computed(() =>
    installationFailureReasonKey(this.deviceDetail()?.installationFailureReason),
  );
  /** Translation key of the resolved installation state; null while no detail is loaded or for a
   * stage this portal does not know, so the template falls back to the raw API text. */
  protected readonly installationStatusKey = computed(() => {
    const detail = this.deviceDetail();
    if (!detail) return null;
    const { installationState } = resolveDeviceStates(detail);
    return installationState ? INSTALLATION_STATE_LABEL_KEYS[installationState] : null;
  });
  private readonly translate = inject(TranslateService);

  protected readonly APP_PATHS = APP_PATHS;

  // One channel can feed several points of one installation, so the installation list is
  // deduplicated from the channel-usage rows.
  protected readonly fedInstallations = computed<ChannelUsageEntry[]>(() => {
    const seen = new Set<string>();
    return (this.channelUsage()?.usages ?? []).filter(usage => {
      if (seen.has(usage.installationOid)) return false;
      seen.add(usage.installationOid);
      return true;
    });
  });

  protected readonly mapDeviceStatusToBadgeStatus = mapDeviceStatusToBadgeStatus;

  /** No ICCID means there is nothing to look up at 1NCE, so the SIM fields and their actions are
   * replaced by an empty state rather than shown as a grid of blanks. */
  protected readonly hasSim = computed(() => !!this.deviceDetail()?.simIccid?.trim());

  /** A Smart Connect device reaches the platform over the customer's own network and has no SIM or
   * LAN of its own, so the whole Network card — including its connection test — is left out rather
   * than shown empty. Protocol is the same device-type capability signal the connection test
   * already keys on; `mqtt` is what `dm.device_type.protocol` calls this family. */
  protected readonly showNetworkCard = computed(
    () => !SIMLESS_PROTOCOLS.has((this.deviceDetail()?.protocol ?? '').toLowerCase()),
  );

  protected readonly topUpVolumeMb = TOP_UP_VOLUME_MB;

  /** Null until a sync has recorded a quota, which is what leaves the bar out rather than drawing an
   * empty one. */
  protected readonly simData = computed<SimData | null>(() => {
    const remainingMb = this.deviceDetail()?.simDataRemainingMb;
    const totalMb = this.deviceDetail()?.simDataTotalMb;
    if (remainingMb == null || totalMb == null || totalMb <= 0) return null;
    return {
      remainingMb,
      totalMb,
      remainingLabel: formatMb(remainingMb),
      totalLabel: formatMb(totalMb),
    };
  });

  /** How much of the activation → end span is still to run. Null when either end is missing or the
   * range is degenerate — a SIM whose dates make no sense gets no bar rather than a misleading one. */
  protected readonly simLifetime = computed<SimLifetime | null>(() => {
    const activationDate = parseSimDate(this.deviceDetail()?.simActivationDate);
    const endDate = parseSimDate(this.deviceDetail()?.simEndDate);
    if (!activationDate || !endDate) return null;

    const totalMs = endDate.getTime() - activationDate.getTime();
    if (totalMs <= 0) return null;

    const now = new Date();
    // Clamped both ways: an expired SIM empties the bar, and one whose activation date is still in
    // the future fills it rather than overflowing the track.
    const remainingMs = Math.min(Math.max(endDate.getTime() - now.getTime(), 0), totalMs);
    return {
      activationDate,
      endDate,
      remainingDays: Math.round(remainingMs / MS_PER_DAY),
      totalDays: Math.round(totalMs / MS_PER_DAY),
      remaining: lifetimeRemaining(now, endDate),
      expired: endDate.getTime() <= now.getTime(),
    };
  });

  onClickCompany(partnerOid: string) {
    this.companyClicked.emit(partnerOid);
  }
  onLocationClick(locationId: number) {
    this.locationClicked.emit(locationId);
  }

  onRefreshSimClick(): void {
    this.refreshSimClicked.emit();
  }

  onTopUpSimClick(): void {
    const iccid = this.deviceDetail()?.simIccid;
    if (!iccid) return;

    const data: ConfirmWarningDialogData = {
      title: this.translate.instant('devices.action.topUpSimConfirmTitle'),
      message: this.translate.instant('devices.action.topUpSimConfirmMessage', { iccid }),
      confirmLabel: this.translate.instant('devices.action.topUpSimConfirmButton'),
      cancelLabel: this.translate.instant('devices.action.topUpSimCancelButton'),
    };

    this.dialog
      .open(ConfirmWarningDialogComponent, { data })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed === true) {
          this.topUpSimConfirmed.emit();
        }
      });
  }

  onExtendSimClick(): void {
    const iccid = this.deviceDetail()?.simIccid;
    if (!iccid) return;

    const data: ConfirmWarningDialogData = {
      title: this.translate.instant('devices.action.extendSimConfirmTitle'),
      message: this.translate.instant('devices.action.extendSimConfirmMessage', { iccid }),
      confirmLabel: this.translate.instant('devices.action.extendSimConfirmButton'),
      cancelLabel: this.translate.instant('devices.action.extendSimCancelButton'),
    };

    this.dialog
      .open(ConfirmWarningDialogComponent, { data })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed === true) {
          this.extendSimConfirmed.emit();
        }
      });
  }
}
